/**
 * PermitExplainer — progressive-disclosure explainer for PERMIT (P).
 *
 * PERMIT neutralizes the vulnerability class "operation performed without
 * authority" by typing every operation as an object of the slice category
 * C/Auth, where a morphism *is* the authority grant. An unauthorized
 * operation is not an object of the slice, so it is structurally
 * inexpressible — exercise and grant coincide definitionally.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { PERMIT_META } from "@csk/core";
import { THEOREM_COLOR, withAlpha } from "../../design-system/tokens.js";
import { MathInline, tex } from "../../components/math/index.js";
import {
  ExplainerCard,
  SliceCategoryDiagram,
  type ExplainerCrossLink,
  type ExplainerIdentity,
  type ExplainerTierContent,
} from "../shared/index.js";
import { TheoremMetaStrip } from "./TheoremMetaStrip.js";
import { TIER_ONE_IMMUNITY_LINK } from "./tierOneCrossLinks.js";

const ACCENT = THEOREM_COLOR.PERMIT;

const IDENTITY: ExplainerIdentity = {
  id: "explainer-permit",
  kind: "SSM · Neutralization Theorem (S1)",
  title: "PERMIT",
  subtitle: "P · Slice category · IMMUNE · G1 Authority",
  accent: ACCENT,
};

const CROSS_LINKS: readonly ExplainerCrossLink[] = [
  { label: "Slice category (structure)", href: "#explainer-slice-category", accent: ACCENT },
  { label: "COMPOSE (G1 sibling)", href: "#explainer-compose", accent: THEOREM_COLOR.COMPOSE },
  { label: "SEPARATE", href: "#explainer-separate", accent: THEOREM_COLOR.SEPARATE },
  TIER_ONE_IMMUNITY_LINK,
];

const TIERS: ExplainerTierContent = {
  tldr: (
    <p>
      <strong>PERMIT</strong> says: doing the thing and being allowed to do the thing are the same
      thing, by construction. An action without authority is not an action you can name.
    </p>
  ),
  intuition: (
    <>
      <p>
        Most systems check authority with a guard: run the operation, ask "is this allowed?", and
        bail if not. The classic failure is the missing or mis-ordered guard — the operation runs
        before, or instead of, the check.
      </p>
      <p>
        CSK removes the guard entirely. Every operation is an object of the slice category{" "}
        <MathInline>{tex`C/\mathrm{Auth}`}</MathInline>, and its very existence is a morphism into
        the authority object. There is no "operation without a grant" to forget to check, because
        exercise <em>is</em> grant — the unauthorized action is unconstructable in the source.
      </p>
    </>
  ),
  formal: (
    <>
      <p>
        Fix an authority object <MathInline>{tex`\mathrm{Auth}`}</MathInline> in the kernel category{" "}
        <MathInline>{tex`C`}</MathInline>. An operation is an object of the slice{" "}
        <MathInline>{tex`C/\mathrm{Auth}`}</MathInline>: a pair{" "}
        <MathInline>{tex`(X, p : X \to \mathrm{Auth})`}</MathInline> where{" "}
        <MathInline>{tex`p`}</MathInline> is the authority witness. A morphism{" "}
        <MathInline>{tex`(X, p) \to (Y, q)`}</MathInline> is a map{" "}
        <MathInline>{tex`X \to Y`}</MathInline> commuting with the witnesses.
      </p>
      <p style={{ marginTop: "0.5rem" }}>
        An object with no morphism to <MathInline>{tex`\mathrm{Auth}`}</MathInline> is simply not in{" "}
        <MathInline>{tex`C/\mathrm{Auth}`}</MathInline>. There is no slice object whose authority
        witness is absent — the unauthorized operation cannot be written. <code>PERMIT</code> is a
        projection of the Categorical Immunity Theorem, not a standalone result.
      </p>
    </>
  ),
  relevance: (
    <>
      <p>
        <strong>Vulnerability class neutralized:</strong> missing or improper authorization — acting
        without a valid grant (CWE-862, CWE-863, CWE-441; e.g., CVE-2021-44228 lookup authority).
        Realized by <em>structural inexpressibility</em>: the ungranted operation is not an object
        of the slice.
      </p>
      <p>
        <strong>Theorem-collision check.</strong> If you call a raw effect outside{" "}
        <MathInline>{tex`C/\mathrm{Auth}`}</MathInline> (e.g., a foreign function that touches the
        OS directly), you have left the kernel category. PERMIT is then vacuous on that call — the
        witness requirement has gone away, not been bypassed.
      </p>
      <p>
        <strong>Seam type</strong> (per <code>theory/structural/SEAM-THEORY.md</code>):{" "}
        {PERMIT_META.seamType}. <strong>Bridge</strong>: capability grant (governance artifact).
        PERMIT pairs with the <code>DELEGATE</code> SDP (slice{" "}
        <MathInline>{tex`\dashv`}</MathInline> coslice): authority use is not authority grant.
      </p>
    </>
  ),
  references: [
    {
      label: "PERMIT theorem card",
      path: "docs/reference/theorem-cards/PERMIT.md",
    },
    {
      label: "Kernel module (main theorem PERMIT-neutralized)",
      path: `agda/${PERMIT_META.agdaSource}`,
      note: `PERMIT_META.agdaSource is "${PERMIT_META.agdaSource}"; the repo path is prefixed with "agda/". PERMIT-neutralized at line 187 (module lines 92–306).`,
    },
    {
      label: "Theorem Canon (§4.1 IMMUNE; §7 DELEGATE SDP)",
      path: "docs/reference/Theorem-Canon.md",
    },
  ],
};

const DIAGRAM = (
  <>
    <TheoremMetaStrip
      theoremName="PERMIT"
      code="P"
      structure="Slice category"
      mode="immune"
      generator="G1"
      seamType={PERMIT_META.seamType}
      bridgeArtifact="capability grant"
      theoremColor={ACCENT}
      washColor={withAlpha(ACCENT, "1A")}
    />
    <div style={{ marginTop: "0.5rem" }}>
      <SliceCategoryDiagram
        accent={ACCENT}
        ariaLabel="Slice category C/Auth — X→Auth and Y→Auth are authorized objects; Z has no morphism to Auth and is not in the slice"
      />
    </div>
  </>
);

export function PermitExplainer(): React.JSX.Element {
  return (
    <ExplainerCard identity={IDENTITY} tiers={TIERS} diagram={DIAGRAM} crossLinks={CROSS_LINKS} />
  );
}
